import { memo, useState } from "react";
import { Modal, Input, message } from "antd";
import styled from "styled-components";
import { useAtomValue } from "jotai";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { InvitationAPIs } from "~/common/typescript/apis/InvitationAPIs";
import { AC_Project_Role } from "~/common/typescript/services/access_control/AC_Project";
import { InvitationType } from "~/common/typescript/models/Invitation";

interface InviteMemberModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (phoneNumber: string) => void;
}

const InviteMemberModal = ({ visible, onClose, onSuccess }: InviteMemberModalProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const currentProject = useAtomValue(currentProjectAtom);

  const handleOk = async () => {
    if (!phoneNumber.trim()) {
      message.warning('Vui lòng nhập số điện thoại');
      return;
    }

    // Validate phone number format (basic validation)
    const phoneRegex = /^[0-9]{9,11}$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      message.warning('Số điện thoại không hợp lệ (9-11 chữ số)');
      return;
    }

    if (!currentProject?.id) {
      message.error('Không tìm thấy dự án hiện tại');
      return;
    }

    try {
      setLoading(true);
      
      // Call API to send invitation
      await InvitationAPIs.remote_sendInvitation({
        identifier: phoneNumber.trim(),
        projectId: currentProject.id,
        role: AC_Project_Role.PROJECT_COLLECTOR, // Default role for Geocollect project
        type: InvitationType.PROJECT,
      });
      
      message.success(`Đã gửi lời mời đến số điện thoại: ${phoneNumber}`);
      
      if (onSuccess) {
        onSuccess(phoneNumber);
      }
      
      handleClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.message || 'Có lỗi xảy ra khi gửi lời mời';
      message.error(errorMessage);
      console.error('Invite member error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    onClose();
  };

  return (
    <Modal
      title="Mời thành viên vào dự án"
      open={visible}
      onOk={handleOk}
      onCancel={handleClose}
      okText="Gửi lời mời"
      cancelText="Hủy"
      confirmLoading={loading}
    >
      <FormContainer>
        <Label>Số điện thoại:</Label>
        <Input
          placeholder="Nhập số điện thoại"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          onPressEnter={handleOk}
          maxLength={11}
        />
        <HintText>Nhập số điện thoại của người dùng cần mời vào dự án</HintText>
      </FormContainer>
    </Modal>
  );
};

export default memo(InviteMemberModal);

const FormContainer = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #1e293b;
`;

const HintText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
`;
